<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    QuestionnairesService,
    type SuperUserQuestionnaire as Questionnaire,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
  } from '@/components/ui/dialog'
  import { useRoute } from 'vue-router'
  import { EyeIcon, PlusIcon, BoltIcon } from '@heroicons/vue/24/outline'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import QuestionnaireSection from '@/components/admin/questionnaire/editor/QuestionnaireEditorSection.vue'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { Separator } from '@/components/ui/separator'
  import FileField from '@/components/admin/form/FileField.vue'
  import { toast } from '@/components/ui/toast'
  import Button from '@/components/ui/button/Button.vue'

  const { t } = useTranslation({
    en: {
      create: 'Create',
      createWithAI: 'Create questionnaire with AI',
      creatingQuestionnaire: 'Creating questionnaire, this may take a while...',
      enterUserPrompt: 'Enter AI prompt',
      questionnaireCreated: 'Questionnaire created with AI',
      questionnaireCreatedDescription:
        'The questionnaire has been created with AI, it is however not yet saved',
      uploadFile: 'Upload file',
      uploadToCreateQuestionnaire: 'Upload a pdf/word file to create a questionnaire using AI',
      uploadHint: 'Upload a pdf/word file or drag and drop it here',
      userPrompt: 'AI Prompt',
      or: 'or',
      save: 'Save Questionnaire',
      unselectedOption: 'None',
      addSection: 'Add Section',
      newSection: 'New section'
    },
    de: {
      create: 'Erstellen',
      createWithAI: 'Fragebogen mit KI erstellen',
      creatingQuestionnaire: 'Fragebogen wird erstellt, dies kann einige Zeit dauern...',
      enterUserPrompt: 'KI Prompt eingeben',
      questionnaireCreated: 'Fragebogen mit KI erstellt',
      questionnaireCreatedDescription:
        'Der Fragebogen wurde mit KI erstellt, ist jedoch noch nicht gespeichert',
      uploadFile: 'Datei hochladen',
      uploadToCreateQuestionnaire:
        'Laden Sie eine PDF-/Word-Datei hoch, um einen Fragebogen mit KI zu erstellen',
      uploadHint: 'Laden Sie eine PDF-/Word-Datei hoch oder ziehen Sie sie hierher',
      userPrompt: 'KI Prompt',
      or: 'oder',
      save: 'Fragebogen speichern',
      unselectedOption: 'Keine',
      addSection: 'Abschnitt hinzufügen',
      newSection: 'Neuer Abschnitt'
    }
  })

  const route = useRoute()
  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const questionnaire = computed<Questionnaire | null>(() => questionnaireEditorStore.questionnaire)
  const organisations = computed<Organisation[]>(
    () => questionnaireEditorStore.organisations as Organisation[]
  )
  const categoryOptions = computed<Option[]>(() => questionnaireEditorStore.categoryOptions)
  const lawFieldsOptions = computed<Option[]>(() => questionnaireEditorStore.lawFieldsOptions)
  const lawTopicOptions = computed<Option[]>(() => questionnaireEditorStore.lawTopicOptions)

  const organizationOptions = computed<Option[]>(() => {
    const options =
      organisations.value?.map((org) => ({
        value: org.uuid,
        title: org.name || ''
      })) || []

    // Include the current organization if it's not already in the list
    if (
      questionnaire.value?.org &&
      !options.find((opt) => opt.value === questionnaire.value?.org?.uuid)
    ) {
      options.unshift({
        value: questionnaire.value.org.uuid,
        title: questionnaire.value.org.name || ''
      })
    }

    return options
  })

  // Computed property for organization UUID (for form binding)
  const selectedOrgUuid = computed({
    get: () => questionnaire.value?.org?.uuid || null,
    set: (value: string | null) => {
      if (questionnaire.value) {
        const orgObj = value ? organisations.value?.find((org) => org.uuid === value) : null
        ;(questionnaire.value as any).org = orgObj
      }
    }
  })

  const uploadedFile = ref<File | null>(null)
  const userPrompt = ref('')
  const search = ref('')

  // Computed properties for mutual exclusivity
  const isOrgFieldDisabled = computed(() => questionnaire.value?.is_jupus_template ?? false)
  const isJupusTemplateFieldDisabled = computed(() => true)

  // Debounced organization search function
  const searchOrganizations = async (query: string, oldQuery: string) => {
    if (query !== oldQuery) {
      await questionnaireEditorStore.searchOrganizations(query)
    }
  }

  const isModalOpen = ref(false)
  const isLoading = ref(false)
  const hasChanges = ref(false)

  watch(
    questionnaire,
    (newValue, oldValue) => {
      if (oldValue !== null || newValue === null) {
        // Do not set hasChanges on initial load when old value is null
        hasChanges.value = true
      }
    },
    { deep: true }
  )

  // Ensure mutual exclusivity between organization assignment and Jupus template
  watch(
    () => questionnaire.value?.is_jupus_template,
    (isJupusTemplate) => {
      if (isJupusTemplate) {
        selectedOrgUuid.value = null
      }
    }
  )

  // Clear Jupus template when organization is assigned
  watch(
    () => questionnaire.value?.org,
    (org) => {
      if (org && questionnaire.value && questionnaire.value.is_jupus_template) {
        questionnaire.value.is_jupus_template = false
      }
    }
  )

  onMounted(async () => {
    await questionnaireEditorStore.init(route.params.uuid as string)
  })

  const saveQuestionnaire = async () => {
    try {
      // Create a copy of the questionnaire for the request, excluding read-only fields
      const {
        org,
        uuid,
        theme_color,
        created_by_name,
        questionnaire_creation_token,
        org_questionnaire_creation_token,
        ...requestData
      } = questionnaire.value!

      const finalRequestData = {
        ...requestData,
        org_uuid: selectedOrgUuid.value
      }

      await QuestionnairesService.questionnairesUpdate({
        uuid: questionnaire.value!.uuid,
        requestBody: finalRequestData
      })
      saveSuccessToast()
      hasChanges.value = false
    } catch (error: any) {
      console.error('Error saving questionnaire:', error)

      if (error.body) {
        // Format validation errors for display
        const errorParts: string[] = []

        // Process the direct JSON structure we're receiving
        const formatJsonErrors = (data: any) => {
          // Handle law_field errors
          if (data.law_field && Array.isArray(data.law_field)) {
            errorParts.push(`⚠️ Law Field: ${data.law_field.join(', ')}`)
          }
          // Handle law_topic errors
          if (data.law_topic && Array.isArray(data.law_topic)) {
            errorParts.push(`⚠️ Law Topic: ${data.law_topic.join(', ')}`)
          }

          // Handle schema errors
          if (data.schema && Array.isArray(data.schema)) {
            data.schema.forEach((section: any, sectionIndex: number) => {
              if (section.items && Array.isArray(section.items)) {
                section.items.forEach((item: any, itemIndex: number) => {
                  if (Object.keys(item).length > 0) {
                    const questionErrors: string[] = []

                    // Process each error in the question
                    Object.entries(item).forEach(([field, messages]: [string, any]) => {
                      if (Array.isArray(messages)) {
                        questionErrors.push(`• ${field}: ${messages.join(', ')}`)
                      }
                    })

                    if (questionErrors.length > 0) {
                      // Get the question label if available
                      let questionText = ''
                      if (questionnaire.value?.schema?.[sectionIndex]?.items?.[itemIndex]?.label) {
                        const fullText =
                          questionnaire.value.schema[sectionIndex].items[itemIndex].label
                        // Get first 30 characters or less if the text is shorter
                        questionText =
                          fullText.length > 30
                            ? `"${fullText.substring(0, 30)}..."`
                            : `"${fullText}"`
                      }

                      errorParts.push(
                        `⚠️ Question ${itemIndex + 1} in Section ${sectionIndex + 1}${questionText ? ': ' + questionText : ':'}`
                      )
                      errorParts.push(...questionErrors)
                    }
                  }
                })
              }
            })
          }

          // Handle other top-level errors
          Object.entries(data).forEach(([key, value]) => {
            if (
              key !== 'law_topic' &&
              key !== 'schema' &&
              key !== 'law_field' &&
              Array.isArray(value)
            ) {
              errorParts.push(`⚠️ ${key}: ${value.join(', ')}`)
            }
          })
        }

        formatJsonErrors(error.body)

        if (errorParts.length > 0) {
          // Join the error parts with double spaces between items for better readability
          const errorMessage = errorParts.join('\n')

          // Display the formatted error message
          toast({
            title: 'Validation Error',
            description: errorMessage,
            variant: 'destructive'
          })
        } else {
          // If we couldn't format the error, display the raw JSON
          toast({
            title: 'Validation Error',
            description: JSON.stringify(error.body, null, 2),
            variant: 'destructive'
          })
        }
      } else {
        // Fallback to generic error toast if we can't parse the error
        genericErrorToast()
      }
    }
  }

  const sendFile = async () => {
    try {
      // Check if questionnaire is null before proceeding
      if (!questionnaire.value) {
        toast({
          title: 'Error',
          description: 'Questionnaire not found',
          duration: 3000
        })
        return
      }

      isLoading.value = true
      const response = (await QuestionnairesService.questionnairesGenerateCreate({
        uuid: questionnaire.value.uuid,
        formData: {
          file: uploadedFile.value!,
          prompt: userPrompt.value ?? ''
        }
      })) as Questionnaire

      // Update the questionnaire with the generated data
      if (questionnaire.value) {
        questionnaire.value.title = response.title || questionnaire.value.title
        questionnaire.value.description = response.description || questionnaire.value.description
        questionnaire.value.category = response.category || questionnaire.value.category
        questionnaire.value.law_field = response.law_field || questionnaire.value.law_field
        questionnaire.value.law_topic = response.law_topic || questionnaire.value.law_topic
        questionnaire.value.schema = response.schema || questionnaire.value.schema

        // Save the AI-generated content to the server immediately
        // This prevents data loss if the component remounts or page refreshes
        const {
          org,
          uuid,
          theme_color,
          created_by_name,
          questionnaire_creation_token,
          org_questionnaire_creation_token,
          ...requestData
        } = questionnaire.value

        const finalRequestData = {
          ...requestData,
          org_uuid: selectedOrgUuid.value
        }

        await QuestionnairesService.questionnairesUpdate({
          uuid: questionnaire.value.uuid,
          requestBody: finalRequestData
        })
      }

      toast({
        title: t('questionnaireCreated'),
        description: t('questionnaireCreatedDescription'),
        duration: 3000
      })
    } catch (error) {
      isModalOpen.value = false
      isLoading.value = false
      console.error('Error creating questionnaire with AI:', error)
      genericErrorToast()
    } finally {
      uploadedFile.value = null
      isModalOpen.value = false
      isLoading.value = false
    }
  }

  const previewQuestionnaire = async () => {
    try {
      // Save the questionnaire first if there are changes
      if (hasChanges.value) {
        await saveQuestionnaire()
      }

      // Open preview in new tab
      window.open(`/client/portal/questionnaire/preview/${questionnaire.value!.uuid}`, '_blank')
    } catch (error) {
      console.error('Error saving questionnaire before preview:', error)
      // Even if save fails, we can still show the preview with a warning
      // The user might want to see what the current saved version looks like
      window.open(`/client/portal/questionnaire/preview/${questionnaire.value!.uuid}`, '_blank')
    }
  }
</script>

<template>
  <div
    v-if="questionnaire"
    class="fixed bottom-0 left-[260px] right-0 top-[59px] flex flex-col overflow-hidden bg-gray-100"
  >
    <div class="flex-none space-y-5 border-b border-gray-200 bg-white p-5 shadow-sm">
      <!-- Page Header -->
      <div class="flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Questionnaires', to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_LIST },
            {
              title: `Details - ${questionnaire.title}`,
              to: ROUTES.ADMIN.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                ':uuid',
                questionnaire.uuid as string
              )
            }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <Dialog v-model:open="isModalOpen">
            <DialogTrigger as-child>
              <Button
                variant="secondary"
                size="default"
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm
                  font-medium hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                @click="isModalOpen = true"
              >
                <BoltIcon class="h-4 w-4" />
                {{ t('createWithAI') }}
              </Button>
            </DialogTrigger>
            <DialogContent
              v-if="isLoading"
              class="flex flex-col items-center"
            >
              <DialogTitle></DialogTitle>
              <div
                class="text-surface mt-2 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid
                  border-current border-e-transparent align-[-0.125em]
                  motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span
                  class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0
                    ![clip:rect(0,0,0,0)]"
                >
                  Loading...
                </span>
              </div>
              <DialogDescription>
                <h3 class="text-lg">{{ t('creatingQuestionnaire') }}</h3>
              </DialogDescription>
            </DialogContent>
            <DialogContent
              v-else
              class="sm:max-w-[425px]"
            >
              <DialogHeader>
                <DialogTitle>{{ t('createWithAI') }}</DialogTitle>
                <DialogDescription>
                  {{ t('uploadToCreateQuestionnaire') }}
                </DialogDescription>
              </DialogHeader>
              <FileField
                v-model="uploadedFile"
                name="file"
                class="overflow-hidden"
                :allowed-file-extensions="['pdf', 'docx']"
                :hint="t('uploadHint')"
              />
              <Separator
                class="my-4"
                :label="t('or')"
              />
              <TextAreaField
                v-model="userPrompt"
                name="user_prompt"
                :placeholder="t('enterUserPrompt')"
              />
              <DialogFooter>
                <button
                  class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-2
                    text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus-visible:bg-purple-700
                    focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                  type="submit"
                  :disabled="!uploadedFile && !userPrompt"
                  @click="sendFile"
                >
                  {{ t('create') }}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            variant="secondary"
            size="default"
            class="inline-flex h-10 items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm
              font-medium hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
            @click="previewQuestionnaire"
          >
            <EyeIcon class="size-4 shrink-0" />
            Preview
          </Button>
          <Button
            variant="default"
            size="default"
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-purple-600 px-4 py-2 text-sm
              font-medium hover:bg-purple-700 focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-purple-700 disabled:pointer-events-none disabled:opacity-50"
            @click="saveQuestionnaire"
          >
            {{ t('save') }}
          </Button>
        </div>
      </div>
    </div>
    <div class="flex flex-1 flex-col gap-5 overflow-y-auto p-5">
      <div class="flex flex-col rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Questionnaire Details</h3>
        <div class="mb-4 w-full md:w-1/2">
          <InputField
            v-model="questionnaire.title"
            label="Title"
            name="title"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <TextAreaField
            v-model="questionnaire.description"
            label="Description"
            name="description"
            rows="2"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.category"
            label="Category"
            name="category"
            :options="categoryOptions"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.law_field"
            label="Law field"
            name="law_field"
            :options="lawFieldsOptions"
          />
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <SelectField
            v-model="questionnaire.law_topic"
            label="Law Topic"
            name="law_field"
            :options="lawTopicOptions"
          />
        </div>
      </div>
      <div class="flex flex-col rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Organization</h3>
        <div class="mb-4 w-full pt-4 md:w-1/2">
          <SelectField
            v-model="selectedOrgUuid"
            label="Assigned Organization"
            name="org"
            :options="organizationOptions"
            :is-filterable="true"
            :input-action="searchOrganizations"
            :allow-unselect="true"
            :unselected-option-text="t('unselectedOption')"
            :disabled="isOrgFieldDisabled"
          />
          <p
            v-if="isOrgFieldDisabled"
            class="mt-1 text-xs text-gray-500"
          >
            Cannot assign to organization while Jupus Template is enabled
          </p>
        </div>
        <div class="mb-4 w-full md:w-1/2">
          <CheckboxField
            v-model="questionnaire.is_jupus_template"
            label="Jupus Template"
            name="is_jupus_template"
            :disabled="isJupusTemplateFieldDisabled"
          />
          <p
            v-if="isJupusTemplateFieldDisabled"
            class="mt-1 text-xs text-gray-500"
          >
            <!-- Cannot enable Jupus Template while assigned to an organization -->
            This is temporarily disabled until the self servive questionnaire is released
          </p>
          <p
            v-else-if="!questionnaire.is_jupus_template"
            class="mt-1 text-xs text-gray-500"
          >
            If checked, this questionnaire will be available as a Jupus template for all
            organizations
          </p>
        </div>
      </div>
      <div class="rounded-xl border bg-white p-4 shadow-sm md:p-5">
        <h3 class="text-lg font-bold text-gray-800">Questions</h3>
        <div class="mb-4 mt-4">
          <div class="flex justify-start">
            <button
              type="button"
              class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
                font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                disabled:pointer-events-none disabled:opacity-50"
              @click="questionnaireEditorStore.addSection(0, t('newSection'))"
            >
              <PlusIcon class="size-4 shrink-0" />
              {{ t('addSection') }}
            </button>
          </div>
          <div
            v-for="(section, index) in questionnaire.schema"
            :key="section.uuid"
          >
            <QuestionnaireSection :uuid="section.uuid" />
            <div class="flex justify-start">
              <button
                type="button"
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
                  font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                  disabled:pointer-events-none disabled:opacity-50"
                @click="questionnaireEditorStore.addSection(index + 1, t('newSection'))"
              >
                <PlusIcon class="size-4 shrink-0" />
                {{ t('addSection') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .scrollable-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    margin-top: 0.5rem;
  }

  /* For Webkit browsers like Chrome/Safari */
  .scrollable-container::-webkit-scrollbar {
    width: 8px;
  }

  .scrollable-container::-webkit-scrollbar-track {
    background: #f7fafc;
  }

  .scrollable-container::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 4px;
    border: 2px solid #f7fafc;
  }

  /* For Internet Explorer */
  .scrollable-container {
    -ms-overflow-style: -ms-autohiding-scrollbar;
  }
</style>
